import React, { useState } from 'react';
import styled from 'styled-components';
import { FiEdit, FiHash, FiCalendar, FiTruck, FiTag, FiCreditCard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';
import ImageCarousel from './ImageCarousel';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const ImageSection = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.gray[100]} 0%, ${theme.colors.gray[200]} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[500]};
  font-size: ${theme.fontSizes.xl};
`;

const StatusBadge = styled.div`
  position: absolute;
  top: ${theme.spacing[3]};
  right: ${theme.spacing[3]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${props => props.$emEstoque ? theme.colors.success[500] : theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 768px) {
    padding: ${theme.spacing[3]};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[3]};
  gap: ${theme.spacing[2]};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[2]};
    margin-bottom: ${theme.spacing[2]};
  }
`;

const VehicleInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 4.3875rem; /* 1.125rem * 1.3 * 3 = altura para 3 linhas */
  max-height: 4.3875rem;

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.base};
    min-height: 3.9rem; /* 1rem * 1.3 * 3 = altura para 3 linhas */
    max-height: 3.9rem;
  }
`;

const Subtitle = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[2]};
  margin: 0;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[1]};
  }
`;

const SubtitleLeft = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.xs};
  }
`;

const SubtitleCode = styled.span`
  display: flex;
  justify-content: flex-end;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.xs};
    justify-content: flex-start;
  }
`;

const Price = styled.div`
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
    width: 100%;
  }
`;

const PriceValue = styled.div`
  color: ${theme.colors.primary[600]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.lg};
  }
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[3]};
    margin-bottom: ${theme.spacing[3]};
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.xs};
  }
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};

  @media (max-width: 768px) {
    font-size: ${theme.fontSizes.sm};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: auto;

  @media (max-width: 768px) {
    gap: ${theme.spacing[2]};
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  min-height: 44px;

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    font-size: ${theme.fontSizes.xs};
    min-height: 40px;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const VeiculoCard = ({ veiculo, onImageClick }) => {
  const navigate = useNavigate();

  const handleImageClickWrapper = () => {
    if (onImageClick) {
      onImageClick(veiculo.imagens);
    }
  };

  const formatPrice = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatYear = (ano) => {
    return ano ? `${ano}` : 'N/A';
  };

  const formatKm = (km) => {
    if (!km) return 'N/A';
    return new Intl.NumberFormat('pt-BR').format(km) + ' km';
  };

  const handleEdit = () => {
    navigate(`/veiculos/${veiculo.uuid || veiculo.id}/editar`);
  };

  return (
    <>
      <CardContainer>
        <ImageSection>
          {veiculo.imagens && veiculo.imagens.length > 0 ? (
            <ImageCarousel 
              images={veiculo.imagens}
              onImageClick={handleImageClickWrapper}
            />
          ) : (
            <ImagePlaceholder>
              <FiTruck />
            </ImagePlaceholder>
          )}
          <StatusBadge $emEstoque={veiculo.em_estoque}>
            {veiculo.em_estoque ? 'Em Estoque' : 'Vendido'}
          </StatusBadge>
        </ImageSection>

        <ContentSection>
          <Header>
            <VehicleInfo>
              <Title>{veiculo.marca} {veiculo.modelo}</Title>             
            </VehicleInfo>
            <Price>
              <PriceValue>{veiculo.valor ? formatPrice(veiculo.valor) : 'N/A'}</PriceValue>
            </Price>
          </Header>

          <Details>
            <SubtitleLeft>
              <DetailIcon><FiCreditCard /></DetailIcon>
              <span>{veiculo.placa || 'N/A'}</span>
            </SubtitleLeft>
            <SubtitleLeft>
              <DetailIcon><FiHash /></DetailIcon>
              <span>{veiculo.code || 'N/A'}</span>
            </SubtitleLeft>
          </Details>

          <Details>
            <DetailItem>
              <DetailIcon><FiCalendar /></DetailIcon>
              <span>{formatYear(veiculo.ano_modelo)}</span>
            </DetailItem>
            <DetailItem>
              <DetailIcon><FiTruck /></DetailIcon>
              <span>{formatKm(veiculo.km)}</span>
            </DetailItem>
          </Details>

          <Actions>
            <ActionButton $primary onClick={handleEdit}>
              <ActionIcon><FiEdit /></ActionIcon>
              Editar
            </ActionButton>
          </Actions>
        </ContentSection>
      </CardContainer>
    </>
  );
};

export default VeiculoCard;
